// Spillets styring: hvad et tryk eller træk betyder lige nu, ringmenuen og tastaturet.
// Prioritet for tryk: trussel (jag væk, også med åben ring eller valgt værktøj), node (vælg A, så B),
// ledning (montør eller menu), tom grund (dobbelttryk centrerer kortet). Ligger en ledning under en
// node, vinder ledningen, når fingeren er tættere på den, når den er knækket, når Opgradér er valgt,
// eller når træneren peger på netop den.

import type { Sfx, SfxKind } from '../audio/sfx';
import type { Game } from '../game';
import type { P } from '../render/geo';
import type { Scene } from '../render/scene';
import type { ActionKind, ApplyError, Intent, Line, PowerId, State } from '../sim';
import { check, exposedLines, REINFORCE_ALL } from '../sim/actions';
import { roleOf } from '../sim/roles';
import { cloneState } from '../sim/state';
import { applyNow } from '../sim/step';
import { cardOpen, isShooable } from '../sim/threats';
import { demolishRefund, lineById, nodeById, REINFORCE_COST, segmentInBand, slotsFree, stationCost, UNDO_TICKS } from '../sim/grid';
import type { BottomBar, Tool } from '../ui/bottombar';
import { ICON } from '../ui/icons';
import type { RingArea, RingItem, RingMenu } from '../ui/ring';
import { CARD_FIRST, CARD_FIRST_SHORT, chaseText, errorText, THREAT_NAMES } from '../ui/text';
import type { Toast } from '../ui/toast';
import { chooseBuild, shortReason } from './build';
import { Pointer } from './pointer';
import type { GestureTarget } from './pointer';

/** En handling, der venter på afspil i Orkans pause. */
interface Plan {
  kind: ActionKind;
  target?: string;
  opt?: string;
  /** Ledninger: de to ender, som spilleren trak dem (til den stiplede streg). */
  pair?: [string, string];
  cost: number;
}

/** Det, Orkan lader en planlægge i pause. Resten (montør, kort, gave) må altid gøres i pause. */
const PLANNABLE: ReadonlySet<ActionKind> = new Set<ActionKind>([
  'build_line',
  'upgrade_line',
  'upgrade_station',
  'build_battery',
  'build_flywheel',
  'reinforce_mast',
  'shoo_threat',
]);

export interface ControlsCtx {
  game: Game;
  scene: Scene;
  ring: RingMenu;
  toast: Toast;
  bar: BottomBar;
  sfx: Sfx;
  stage: HTMLElement;
  bubble: HTMLElement;
  /** Spillet er aktivt (ingen skærm foran). */
  active(): boolean;
  togglePause(): void;
  toggleSpeed(): void;
  callYear(): void;
  escape(): boolean;
  /** Det frie felt til ringmenuen (sidekoordinater): mellem HUD, værktøjer og power-ups. */
  ringArea?(): RingArea;
  /** Ledningen, træneren peger på lige nu (vinder et tryk, der også rammer en node). */
  adviceLine(): string | null;
  /** Et tryk på et hændelseskort på kortet: vis svarene. */
  card?(id: string): void;
  /** Et tryk valgte en by eller et datacenter: vis bykortet. */
  inspect?(id: string): void;
  /** En handling lykkedes (onboarding-hånden lytter efter første træk, opgradering og forstærkning). */
  onAct?(kind: ActionKind): void;
  /** Haptik: et lille bump, når ledningen griber fat. */
  vibrate?(pattern: number | number[]): void;
  /** Power-ups fra tastaturet (Q, W, E og tal til Nødaflastningens byliste). */
  powers?: { press(id: PowerId): string | null; pressHz(): string | null; pickIndex(i: number): boolean; readonly isPicking: boolean };
  /** Hændelseskortet fra tastaturet (A, S, D). */
  cardPanel?: { answerIndex(i: number): boolean; readonly isOpen: boolean };
  /** Spor: coop: bed makkeren om et træk, kun den anden rolle må ("Byg her?"). Falsk uden makker. */
  ask?(at: string, kind: ActionKind): boolean;
  /** Kan spørgsmålet sendes lige nu (makker i rummet, eller Volt i solo)? */
  canAsk?(): boolean;
  /** Makkerens navn, når makkeren er Volt (solo). null online og uden makker. */
  partnerName?(): string | null;
}

/** Lyden for hver handling: spillerens egne handlinger lyder straks ved trykket. */
const ACT_SOUND: Partial<Record<ActionKind, SfxKind>> = {
  build_line: 'build',
  upgrade_line: 'upgrade',
  upgrade_station: 'upgrade',
  reinforce_mast: 'reinforce',
  hire_crew: 'crew',
  dispatch_crew: 'crew',
  bury_line: 'upgrade',
  demolish: 'demolish',
  shoo_threat: 'pop',
  build_battery: 'upgrade',
  build_flywheel: 'upgrade',
};

/** Så tæt på en node skal fingeren slippe ledningen. Rundt regnet en fingerspids, også for en ny, lille by. */
const SNAP_PX = 40;
const DOUBLE_TAP_MS = 350;

/**
 * Er mellemrum pause, når fokus står her? Ja for HUD'en, værktøjslinjen og power-ups. Nej inde i
 * hændelseskortet, Volt-panelet, ringmenuen og skærmene: dér trykker mellemrum på knappen som normalt.
 */
function spaceIsPause(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false;
  if (t.isContentEditable || t.tagName === 'SELECT') return false;
  return !t.closest('.evcard, .volt, .ring, .screens, [role="dialog"], .coop');
}

export class Controls implements GestureTarget {
  readonly pointer: Pointer;
  private dragTarget: string | null = null;
  private dragFrom: string | null = null;
  /** Elastikkens seneste segment: én tone pr. stykke, fingeren trækker. */
  private dragSeg = -1;
  private lastEmptyTap: { x: number; y: number; at: number } | null = null;
  /** Shift holdes nede: byg jordkabel frem for luftledning. */
  private shift = false;
  /**
   * IGN-løkke 2: én gestus, én handling. Stod ringen åben, da fingeren landede? Så lukker et stille
   * tryk den og gør ikke andet, men et træk fra en by bygger som altid (ringen åd før trækket).
   */
  private ringAtDown = false;
  /**
   * Truslen, fingeren landede på. En gravko eller en båd sejler videre under fingeren, og et tryk,
   * der slipper ved siden af den, gælder stadig truslen, ikke ledningen eller byen under den.
   */
  private downThreat: { id: string; at: number } | null = null;

  /** Spilleren trækker en ledning lige nu. */
  get dragging(): boolean {
    return this.dragFrom !== null;
  }

  /** Fingeren er nede på en node (før trækket er begyndt) eller trækker: kort, årsskifte og kamera venter. */
  get holding(): boolean {
    return this.dragFrom !== null || this.pointer.holding;
  }

  /** En finger (eller musen) er nede på kortet, uanset hvad den gør: kameraet glider ikke imens. */
  get touching(): boolean {
    return this.holding || this.pointer.pressed;
  }

  constructor(private ctx: ControlsCtx) {
    this.pointer = new Pointer(ctx.stage, this);
    window.addEventListener('keydown', this.key);
    window.addEventListener('keyup', this.keyUp);
    window.addEventListener('blur', () => this.setShift(false));
    ctx.ring.onClose = () => {
      // Ringen lukker: vælg intet.
    };
    // Et træk, der startede på en ringknap, fortsætter på kortet fra det sted, fingeren landede.
    ctx.ring.onDragThrough = (down, now) => {
      const init = (e: PointerEvent): PointerEventInit => ({ pointerId: e.pointerId, pointerType: e.pointerType, isPrimary: e.isPrimary, button: 0, buttons: 1, clientX: e.clientX, clientY: e.clientY, bubbles: true, cancelable: true });
      ctx.stage.dispatchEvent(new PointerEvent('pointerdown', init(down)));
      ctx.stage.dispatchEvent(new PointerEvent('pointermove', init(now)));
    };
  }

  private get game(): Game {
    return this.ctx.game;
  }

  private get scene(): Scene {
    return this.ctx.scene;
  }

  // -------------------------------------------------------------------------
  // Handlinger

  /** Send en handling og giv straks besked, hvis den ikke kan lade sig gøre. */
  act(kind: ActionKind, target?: string, opt?: string): boolean {
    if (!this.game.canBuildNow && !(this.game.paused && this.game.canInPause(kind))) {
      if (this.planning && PLANNABLE.has(kind)) return this.plan(kind, target, opt);
      if (this.game.paused) this.fail(kind, 'paused');
      return false;
    }
    const r = this.game.push(kind, target, opt);
    if (!r.ok) {
      if (r.error === 'paused') this.fail(kind, 'paused');
      return false;
    }
    this.ctx.sfx.play(ACT_SOUND[kind] ?? 'tap');
    if (kind === 'build_line') this.ctx.vibrate?.(15);
    this.ctx.onAct?.(kind);
    return true;
  }

  /** Kaldes af Game, når en handling afvises. */
  fail(kind: ActionKind | null, error: ApplyError | 'paused'): void {
    // Banen er slut: et sent tryk bag resultatkortet siger ingenting (IGN runde 1: "Vent lidt" på tabsskærmen).
    if (this.game.over) return;
    // Solo står uret stille, mens et hændelseskort venter (Spor: kortet). Kortet står fremme og siger
    // det selv; beskeden gælder kun, til kortet er besvaret, og står aldrig bagefter.
    if (error === 'paused' && this.game.cardHold) this.ctx.toast.say(CARD_FIRST, 'warn', 2400, null, null, () => this.game.cardHold);
    // IGN-løkke 2 (runde 2): "Spillet holder pause" stod videre, mens spillet kørte. Beskeden gælder
    // kun, mens uret står stille.
    else if (error === 'paused') this.ctx.toast.say(errorText(kind, error), 'warn', 2400, null, null, () => !this.game.running);
    else this.ctx.toast.say(errorText(kind, error, this.ctx.partnerName?.() ?? null), 'warn');
    this.ctx.sfx.play('error');
    // Et træk, der så godt ud ved slip, men blev afvist i sim'en (nettet nåede at ændre sig): sig det
    // også ved byen, hvor fingeren slap (playtest runde 3: en tom boble og ingen ledning).
    const sent = this.sentBuild;
    this.sentBuild = null;
    if (kind === 'build_line' && error !== 'paused' && sent && performance.now() - sent.at < 2000) {
      const at = this.scene.nodeScreen(sent.to);
      if (at) {
        this.lastFail = shortReason(error, kind);
        this.dropNote(sent.to, at);
      }
    }
  }

  /** Det seneste træk, der blev sendt til sim'en (til boblen, hvis det afvises bagefter). */
  private sentBuild: { to: string; at: number } | null = null;

  /** Et træk, der sluttede ved årsskiftet: bygges, når gaven er valgt. */
  private afterYear: [string, string] | null = null;

  /** Byg trækket fra årsskiftet nu, hvis der er et (kaldes, når gaven er valgt). */
  flushAfterYear(): void {
    const p = this.afterYear;
    this.afterYear = null;
    if (p && this.game.state.phase === 'playing') this.build(p[0], p[1]);
  }

  /**
   * Orkan bygger ikke i pause, men man må gerne planlægge: et træk i pause bliver en stiplet ledning,
   * der bygges i rækkefølge, når uret går igen. IGN-løkke 2: "Orkan er en mur; pausen er kun til at
   * kigge i". Højst tre, så pausen ikke bliver en gratis byggefase. Kun spillerens egen pause, ikke
   * kortets (dér skal kortet besvares først).
   *
   * Runde 2: planen er en rigtig plan. Opgradering, station, batteri, forstærkning og at jage en gæst
   * kan også sættes i kø, og hvert nyt træk tjekkes mod nettet, som det ser ud, når de første er
   * bygget (pladser og kasse). Et træk, der ikke kan lade sig gøre, får samme grund som i spillet
   * ("Fredericia har ikke flere pladser"), ikke "Spillet holder pause". Toasten viser prisen i alt.
   */
  private planned: Plan[] = [];
  static readonly MAX_PLANNED = 3;

  /** Planen, som den står (testkrog). */
  get plannedList(): readonly { kind: ActionKind; target?: string }[] {
    return this.planned.map((p) => (p.target !== undefined ? { kind: p.kind, target: p.target } : { kind: p.kind }));
  }

  get plannedPairs(): readonly [string, string][] {
    const out: [string, string][] = [];
    for (const p of this.planned) if (p.pair) out.push(p.pair);
    return out;
  }

  /** Planlægger spilleren lige nu (egen pause på en sværhed, der ikke bygger i pause)? */
  get planning(): boolean {
    const g = this.game;
    return g.paused && !g.cardHold && !g.over && !g.remote && g.state.phase === 'playing' && !g.canInPause('build_line');
  }

  /** Nettet, som det ser ud, når planen er bygget (til at tjekke det næste træk). */
  private preview(): State {
    const st = cloneState(this.game.state);
    if (!this.planned.length) return st;
    const player = this.game.player;
    const intents: Intent[] = this.planned.map((p, i) => {
      const it: Intent = { player, clientSeq: 1_000_000 + i, kind: p.kind, source: 'human' };
      if (p.target !== undefined) it.target = p.target;
      if (p.opt !== undefined) it.opt = p.opt;
      return it;
    });
    applyNow(st, intents);
    return st;
  }

  /** Sæt en handling i kø til afspil. Svarer altid selv (toast); sand, når den kom i kø. */
  private plan(kind: ActionKind, target: string | undefined, opt: string | undefined, pair?: [string, string]): boolean {
    const g = this.game;
    const same = (p: Plan): boolean =>
      pair && p.pair ? (p.pair[0] === pair[0] && p.pair[1] === pair[1]) || (p.pair[0] === pair[1] && p.pair[1] === pair[0]) : p.kind === kind && p.target === target;
    if (this.planned.some(same)) {
      this.planText();
      return true;
    }
    // At jage en gæst væk er et tryk, ikke et byggeri: det tæller ikke med i de tre (Spor: tillid).
    if (kind !== 'shoo_threat' && this.planned.filter((p) => p.kind !== 'shoo_threat').length >= Controls.MAX_PLANNED) {
      this.lastFail = 'Højst 3';
      this.ctx.toast.say('Højst tre i kø. Tryk afspil, så sker de.', 'warn', 2400, null, null, () => this.planning);
      this.ctx.sfx.play('error');
      return false;
    }
    const c = check(this.preview(), kind, target, opt, g.player);
    if (!c.ok) {
      this.lastFail = shortReason(c.error, kind);
      this.fail(kind, c.error);
      return false;
    }
    const p: Plan = { kind, cost: c.cost };
    if (target !== undefined) p.target = target;
    if (opt !== undefined) p.opt = opt;
    if (pair) p.pair = pair;
    this.planned.push(p);
    if (kind === 'shoo_threat' && target) this.scene.threats.markShooed(target);
    this.ctx.sfx.play('tap');
    this.planText();
    return true;
  }

  /** "2 i kø, 14 kr i alt. Sker, når du trykker afspil." Står kun, mens pausen varer. */
  private planText(): void {
    const n = this.planned.length;
    const kr = this.planned.reduce((a, p) => a + p.cost, 0);
    const what = n === 1 ? (this.planned[0]?.pair ? 'Ledningen' : 'Det') : `${n} i kø`;
    const price = kr > 0 ? `${n === 1 ? ' koster' : ','} ${kr} kr${n === 1 ? '' : ' i alt'}` : '';
    const left = this.game.state.budget - kr;
    const text = n === 1 ? `${what}${price} og sker, når du trykker afspil. ${left} kr tilbage.` : `${what}${price}. Sker, når du trykker afspil. ${left} kr tilbage.`;
    this.ctx.toast.say(text, 'good', 3200, null, null, () => this.planning);
  }

  /**
   * Byg planen, når uret går (kaldes ved afspil og hver frame før uret tæller). En skærm foran eller
   * slut: glem den. Spor: tillid: trækkene udføres straks og i rækkefølge mod nettet med de forrige
   * bygget, præcis som forhåndsvisningen tjekkede dem. Kan et alligevel ikke lade sig gøre (fx er en
   * gæst gået, eller kassen er tom), siger en toast hvilket og hvorfor, og pengene bliver i kassen.
   */
  flushPlanned(): void {
    if (!this.planned.length) return;
    const g = this.game;
    if (g.over || g.state.phase !== 'playing') {
      this.planned = [];
      return;
    }
    if (!g.running) return;
    const list = this.planned;
    this.planned = [];
    const res = g.applyPlan(list);
    const failed: string[] = [];
    let kept = 0;
    res.forEach((err, i) => {
      const p = list[i] as Plan;
      if (err === null) {
        this.ctx.onAct?.(p.kind);
        return;
      }
      kept += p.cost;
      failed.push(this.planFailText(p, err));
    });
    const first = list[res.indexOf(null)];
    if (first) this.ctx.sfx.play(ACT_SOUND[first.kind] ?? 'tap');
    if (!failed.length) return;
    const more = failed.length > 1 ? ` (og ${failed.length - 1} til)` : '';
    const money = kept > 0 ? ` De ${kept} kr blev ikke brugt.` : '';
    this.ctx.toast.say(`${failed[0]}${more}.${money}`, 'warn', 4200);
    this.ctx.sfx.play('error');
  }

  /** "Ledningen Grindsted–Endrup kunne ikke bygges: ingen pladser" (uden punktum). */
  private planFailText(p: Plan, err: ApplyError): string {
    const s = this.game.state;
    const name = (id?: string): string => (id ? (nodeById(s, id)?.name ?? '') : '');
    const lineName = (id?: string): string => {
      const l = id ? lineById(s, id) : undefined;
      return l ? `${name(l.a)}–${name(l.b)}` : '';
    };
    const why = shortReason(err, p.kind).toLowerCase();
    let what: string;
    switch (p.kind) {
      case 'build_line':
        what = p.pair ? `Ledningen ${name(p.pair[0])}–${name(p.pair[1])} kunne ikke bygges` : 'Ledningen kunne ikke bygges';
        break;
      case 'upgrade_line':
        what = `Opgraderingen af ${lineName(p.target) || 'ledningen'} kunne ikke laves`;
        break;
      case 'reinforce_mast':
        what = `Masterne på ${lineName(p.target) || 'ledningen'} kunne ikke forstærkes`;
        break;
      case 'upgrade_station':
        what = `Stationen i ${name(p.target) || 'byen'} kunne ikke bygges`;
        break;
      case 'build_battery':
        what = `Batteriet i ${name(p.target) || 'byen'} kunne ikke bygges`;
        break;
      case 'build_flywheel':
        what = `Svinghjulet i ${name(p.target) || 'byen'} kunne ikke bygges`;
        break;
      case 'shoo_threat': {
        const t = s.threats.find((x) => x.id === p.target);
        const who = t ? (THREAT_NAMES[t.kind] ?? 'Gæsten') : 'Gæsten';
        return `${who} kunne ikke jages væk: ${t ? why : 'den var allerede væk'}`;
      }
      default:
        what = 'Trækket kunne ikke gøres';
    }
    return `${what}: ${why}`;
  }

  build(a: string, b: string): boolean {
    const g = this.game;
    this.sentBuild = null;
    if (g.state.phase === 'yearend' && !g.paused && !g.over) {
      this.afterYear = [a, b];
      return true;
    }
    const planning = !g.canBuildNow && this.planning;
    if (!g.canBuildNow && !planning) {
      const held = g.paused || g.cardHold;
      this.lastFail = g.cardHold ? CARD_FIRST_SHORT : g.paused ? 'Pause' : 'Vent lidt';
      this.fail('build_line', held ? 'paused' : 'wrong_phase');
      return false;
    }
    // I Orkans pause tjekkes trækket mod nettet med planen bygget, så grunden er den samme som i spillet.
    const st = planning ? this.preview() : g.state;
    const c = chooseBuild(st, g.level.mechanics, a, b, this.shift);
    this.lastFail = c.ok ? '' : shortReason(c.error);
    if (!c.ok) {
      if (c.error === 'no_slots' && this.offerStation(a, b, st)) return false;
      if (c.error === 'already_exists' && this.explainExisting(a, b)) return false;
      this.fail('build_line', c.error);
      return false;
    }
    if (planning) return this.plan('build_line', c.target, c.medium, [a, b]);
    const ok = this.act('build_line', c.target, c.medium);
    if (!ok) this.lastFail = g.paused ? 'Pause' : 'Ikke nu';
    else this.sentBuild = { to: b, at: performance.now() };
    return ok;
  }

  /**
   * Ledningen findes allerede, men virker måske ikke: sig hvorfor. IGN-løkke runde 2: "Den er der
   * allerede", mens byen var mørk, lød som om spillet ikke så problemet.
   */
  private explainExisting(a: string, b: string): boolean {
    const s = this.game.state;
    const l = s.lines.find((x) => (x.a === a && x.b === b) || (x.a === b && x.b === a));
    if (!l) return false;
    const dark = [a, b].map((id) => nodeById(s, id)).find((n) => n?.city && !n.phantom && (n.city.out || n.city.servedMw < n.city.demandMw));
    let text = '';
    if (l.status === 'broken') text = 'Ledningen er knækket. En montør kører derud, så snart en er ledig.';
    else if (dark) text = `Ledningen er der, men der kommer ikke strøm nok ind i ${dark.name}. Forbind den til en mølle eller et stærkere net.`;
    else return false;
    this.ctx.toast.say(text, 'warn', 3600);
    this.ctx.sfx.play('error');
    return true;
  }

  /** Den fulde ende af en ledning, der ikke kan bygges: tilbyd en station med ét tryk på toasten. */
  private offerStation(a: string, b: string, s: State = this.game.state): boolean {
    if (!this.game.level.mechanics.includes('station')) return false;
    const full = [a, b].map((id) => nodeById(s, id)).find((n) => n && n.kind !== 'foreign' && slotsFree(s, n) <= 0);
    if (!full) return false;
    const c = check(s, 'upgrade_station', full.id, undefined);
    this.ctx.sfx.play('error');
    // "Station" er værktøjets navn; teksten siger, hvad det giver: flere pladser til ledninger.
    // IGN runde 2: ingen købeknap i en toast. Byens egen menu åbner med "Flere pladser" øverst.
    // IGN-løkke 2, runde 3 (mobil): et træk fra en fuld by åbnede bare byens ring. Toasten ventede bag
    // ringen (et fokuslag), så spilleren så ingen grund. Nu siger ringens titel og boblen ved fingeren
    // det selv, og ingen toast står i kø til bagefter.
    this.lastFail = `${full.name} er fuld`;
    if (c.ok) {
      const at = this.scene.nodeScreen(full.id);
      if (at) this.nodeMenu(full.id, at, `${full.name} er fuld`);
      else this.ctx.toast.say(`${full.name} har ikke flere pladser til ledninger.`, 'warn', 3000);
    } else if (c.error === 'too_poor') {
      this.ctx.toast.say(`${full.name} har ikke flere pladser. ${stationCost(s)} kr giver flere.`, 'warn', 3000);
    } else {
      this.ctx.toast.say(`${full.name} har ikke flere pladser. Byg via en anden by.`, 'warn', 3000);
    }
    return true;
  }

  /** Opgradér en station, og gå tilbage til Byg bagefter. */
  private upgradeStation(id: string): void {
    const planned = this.planning;
    if (!this.act('upgrade_station', id) || planned) return;
    if (this.ctx.bar.tool !== 'build') this.ctx.bar.pick('build');
    const name = nodeById(this.game.state, id)?.name ?? '';
    this.ctx.toast.say(`${name} har nu plads til 6 ledninger.`, 'good', 2000);
  }

  select(id: string | null): void {
    this.scene.selected = id;
    this.scene.targets = id ? this.buildTargets(id) : null;
  }

  /** Noder, en ledning fra `from` kan nå lige nu (rækkevidde, pladser og kasse). */
  private buildTargets(from: string): Set<string> {
    const st = this.game.state;
    const out = new Set<string>();
    for (const n of st.nodes) {
      if (!n.active || n.id === from) continue;
      if (chooseBuild(st, this.game.level.mechanics, from, n.id, this.shift).ok) out.add(n.id);
    }
    return out;
  }

  // -------------------------------------------------------------------------
  // Gestus

  dragSource(p: P): string | null {
    // Kaldes ved hvert pointerdown: husk, hvad fingeren landede på, før noget flytter sig.
    this.ringAtDown = this.ctx.ring.isOpen;
    const under = this.ctx.active() ? this.scene.hitThreat(p) : null;
    this.downThreat = under ? { id: under, at: performance.now() } : null;
    // Et træk fra en node bygger altid, også med Opgradér eller Station valgt: værktøjet skifter selv.
    // Også med ringen åben: trækket lukker den (dragStart), så næste ledning aldrig bliver en panorering.
    if (!this.ctx.active()) return null;
    // Spor: coop: Driftsvagten bygger ikke, så et træk fra en by panorerer bare (et træk, der blev
    // afvist bagefter, var præcis den løgn, co-op-anmelderen fandt).
    if (this.driftOnly) return null;
    // En gæst under fingeren vinder over noden bagved, medmindre fingeren står på selve byen: så er
    // det et træk (IGN-løkke runde 4: en ko ved Erritsø gjorde trækket til en panorering). Et stille
    // tryk jager stadig gæsten væk, for tap() spørger truslen først.
    if (this.scene.hitThreat(p) && !this.scene.hitNodeRecent(p)) return null;
    // Et træk, der starter lidt ved siden af byen, er stadig et træk, ikke en panorering (IGN runde 1:
    // "mislykkede træk flytter kortet"). Ligger en ledning under fingeren, vinder den dog.
    // Spor: telefonkort: også hvor byen stod for et øjeblik siden, hvis kameraet lige gled.
    const id = this.scene.hitNodeRecent(p) ?? (this.pickLine(p) ? null : this.scene.hitNodeRecent(p, 14));
    if (!id) return null;
    const n = nodeById(this.game.state, id);
    return n && n.kind !== 'foreign' ? id : null;
  }

  softTap(p: P): boolean {
    return this.ctx.active() && this.scene.hitThreat(p) !== null;
  }

  tap(p: P, _touch: boolean): void {
    if (!this.ctx.active()) return;
    const s = this.game.state;
    const ringWasOpen = this.ringAtDown || this.ctx.ring.isOpen;
    this.ringAtDown = false;
    const threat = this.scene.hitThreat(p) ?? this.heldThreat();
    this.downThreat = null;
    if (threat) {
      this.ctx.ring.close();
      const th = s.threats.find((t) => t.id === threat);
      if (th?.card) {
        this.ctx.card?.(threat);
        return;
      }
      // En trussel, der ikke kan jages, siger hvad der virker i stedet, også i pause.
      if (th && !isShooable(th.kind)) {
        this.ctx.toast.say(chaseText(th.kind, this.game.level.mechanics.includes('norway')), 'info', 3200, this.scene.threatScreen(threat));
        this.ctx.sfx.play('error');
        return;
      }
      if (this.planning) {
        this.plan('shoo_threat', threat, undefined);
        return;
      }
      const r = this.game.push('shoo_threat', threat);
      if (!r.ok && r.error === 'paused') this.fail('shoo_threat', 'paused');
      if (r.ok) {
        // Vis flugten med det samme; sim'en bekræfter i næste tick.
        this.scene.threats.markShooed(threat);
        this.ctx.sfx.play('pop');
      }
      return;
    }
    // Et tryk ved siden af ringen lukker den og gør ikke andet.
    if (ringWasOpen) {
      this.ctx.ring.close();
      return;
    }
    // IGN-løkke 2, runde 2: et tryk på en storm eller en vindstille gav ingen lyd og ingen tekst.
    // Ikonet vinder over en by eller ledning under det, når fingeren er klart tættere på ikonet end
    // på dem (vindstillen sidder oven på sit vindværk; et tryk midt på værket åbner stadig menuen).
    const other = this.scene.nearThreat(p);
    const ot = other ? s.threats.find((t) => t.id === other) : undefined;
    if (ot && !isShooable(ot.kind) && this.iconWins(ot.id, p)) {
      this.ctx.toast.say(chaseText(ot.kind, this.game.level.mechanics.includes('norway')), 'info', 3200, this.scene.threatScreen(ot.id));
      this.ctx.sfx.play('error');
      return;
    }
    const tool = this.ctx.bar.tool;
    const hitL = this.pickLine(p);
    // Med Station valgt rammer et tryk lidt ved siden af værket stadig værket (en tommelfinger).
    let node = this.scene.hitNode(p) ?? (tool === 'station' ? this.scene.hitNode(p, 22) : null);
    if (!node && tool === 'station') {
      this.ctx.toast.say('Tryk på en by eller et værk for at give den flere pladser.', 'info', 2400);
      return;
    }
    if (node && hitL && !this.scene.selected && tool !== 'station' && this.lineWins(hitL, node, p, tool)) node = null;
    // Spor: coop: Driftsvagtens tryk på en by åbner Driftsvagtens egne knapper og "Byg her?" til
    // makkeren, aldrig et byggevalg, rummet afviser bagefter.
    if (node && this.driftOnly) {
      this.select(null);
      this.driftMenu(node, p);
      return;
    }
    if (node) {
      if (tool === 'station') {
        this.upgradeStation(node);
        return;
      }
      // En fuld node kan ikke bygges fra: tilbyd stationen med det samme.
      const nn = nodeById(s, node);
      if (!this.scene.selected && nn && this.game.level.mechanics.includes('station') && nn.station === 0 && nn.kind !== 'foreign' && slotsFree(s, nn) <= 0) {
        this.nodeMenu(node, p, `${nn.name} er fuld`);
        return;
      }
      const sel = this.scene.selected;
      // Et tryk på en node, der ikke kan nås fra den valgte, vælger den i stedet (og viser dens kort).
      // IGN-løkke runde 2: "jeg trykkede for at se en by, og spillet prøvede at bygge".
      const inspectInstead = (): boolean => {
        if (!sel || sel === node || this.scene.targets?.has(node) || !this.game.canBuildNow) return false;
        const why = chooseBuild(s, this.game.level.mechanics, sel, node, this.shift);
        // Penge og pladser forklares stadig af byggeforsøget; resten (findes, for langt, andet net) er et kig.
        return !why.ok && why.error !== 'too_poor' && why.error !== 'no_slots';
      };
      if (inspectInstead()) {
        this.select(node);
        this.scene.cities.squash(node);
        this.ctx.sfx.play('tap');
        if (nn?.city) this.ctx.inspect?.(node);
        return;
      }
      if (sel && sel !== node) {
        this.build(sel, node);
        this.select(null);
        return;
      }
      if (sel === node) {
        this.select(null);
        this.nodeMenu(node, p);
        return;
      }
      this.select(node);
      this.scene.cities.squash(node);
      this.ctx.sfx.play('tap');
      if (nn?.city) this.ctx.inspect?.(node);
      return;
    }
    const line = hitL;
    if (line) {
      const l = lineById(s, line);
      if (!l) return;
      if (l.status === 'broken') {
        // Montørerne kører selv. Et tryk siger, hvordan det går, og sender en, hvis en er blevet ledig.
        if (s.crews.some((c) => c.task !== 'idle' && c.lineId === line)) this.ctx.toast.say('Montøren er på vej.', 'good', 1600);
        else if (check(s, 'dispatch_crew', line, undefined).ok) this.act('dispatch_crew', line);
        else this.ctx.toast.say('Alle montører er optaget. Hyr en mere.', 'info', 2000);
        return;
      }
      if (tool === 'upgrade') {
        // IGN-løkke 2 (runde 2): værktøjet bliver valgt, så man kan opgradere flere ledninger i træk
        // ("Opgradér er et engangsværktøj; næste tryk åbnede ringen"). En gæst ved ledningen vinder
        // stadig, for tap() spørger truslen først, og et træk fra en by skifter selv til Byg.
        this.act('upgrade_line', line);
        return;
      }
      this.select(null);
      this.lineMenu(l, p);
      return;
    }
    // Dobbelttryk på tom grund: vis hele kortet igen. Det første tryk må gerne fravælge.
    const now = performance.now();
    if (this.scene.selected) {
      this.select(null);
      this.lastEmptyTap = { x: p.x, y: p.y, at: now };
      return;
    }
    const last = this.lastEmptyTap;
    if (last && now - last.at < DOUBLE_TAP_MS && Math.hypot(last.x - p.x, last.y - p.y) < 40) {
      this.lastEmptyTap = null;
      this.scene.recenter();
      return;
    }
    this.lastEmptyTap = { x: p.x, y: p.y, at: now };
  }

  /** Truslen fra pointerdown, hvis den stadig kan jages (højst 1,5 s gammel). */
  private heldThreat(): string | null {
    const d = this.downThreat;
    if (!d || performance.now() - d.at > 1500) return null;
    const t = this.game.state.threats.find((x) => x.id === d.id);
    if (!t || t.shooed || (t.card && !cardOpen(t))) return null;
    return t.phase === 'warning' || (t.kind === 'smaa' && t.phase === 'active') ? t.id : null;
  }

  /** Er trykket klart tættere på truslens ikon end på byen og ledningen under det? */
  private iconWins(threat: string, p: P): boolean {
    const at = this.scene.threatScreen(threat);
    if (!at) return false;
    const d = Math.hypot(at.x - p.x, at.y - p.y) + 8;
    const node = this.scene.hitNode(p);
    if (node && this.scene.nodeDist(node, p) <= d) return false;
    const line = this.pickLine(p);
    return !line || this.scene.lineDist(line, p) > d;
  }

  /** Ledningen under fingeren. Træneren vinder over en anden ledning tæt ved. */
  private pickLine(p: P): string | null {
    const hit = this.scene.hitLine(p);
    const coach = this.ctx.adviceLine();
    if (coach && hit !== coach && this.scene.lineDist(coach, p) <= 32) return coach;
    return hit;
  }

  /** Skal ledningen vinde over noden ved et tryk, der rammer begge? */
  private lineWins(lineId: string, node: string, p: P, tool: Tool): boolean {
    const l = lineById(this.game.state, lineId);
    if (!l) return false;
    if (l.status === 'broken' || tool === 'upgrade' || lineId === this.ctx.adviceLine()) return true;
    // En ledning, der går forbi noden (ikke ud fra den), vinder, når fingeren er tættere på den.
    const own = l.a === node || l.b === node;
    const dl = this.scene.lineDist(lineId, p);
    const dn = this.scene.nodeDist(node, p);
    // Nodens egen ledning vinder ude på midten: et tryk mindst 24 px fra noden og klart tættere på
    // ledningen (IGN-løkke runde 2: "ledningerne kan ikke rammes på en telefon").
    if (own) return dn >= 24 && dl + 8 < dn;
    return dl < dn;
  }

  /** Åbn menuen for en ledning (fx fra hintet, der peger på den). */
  openLine(id: string): void {
    const l = lineById(this.game.state, id);
    const at = this.scene.lineScreen(id);
    if (!l || !at || !this.ctx.active()) return;
    this.select(null);
    if (l.status === 'broken') {
      this.tap(at, true);
      return;
    }
    this.lineMenu(l, at);
  }

  /** Åbn menuen for en node (fx fra hintet om en station). */
  openNode(id: string): void {
    const at = this.scene.nodeScreen(id);
    if (!at || !this.ctx.active()) return;
    this.select(null);
    this.nodeMenu(id, at);
  }

  hover(p: P | null): void {
    if (!p || !this.ctx.active()) {
      this.ctx.stage.style.cursor = '';
      return;
    }
    const hit = this.scene.hitThreat(p) ?? this.scene.hitNode(p) ?? this.scene.hitLine(p);
    this.ctx.stage.style.cursor = hit ? 'pointer' : 'grab';
  }

  dragStart(from: string, p: P, touch: boolean): void {
    if (this.ctx.bar.tool !== 'build') this.ctx.bar.pick('build');
    this.dragFrom = from;
    this.dragSeg = -1;
    this.select(from);
    this.ctx.ring.close();
    this.dragMove(from, p, touch);
  }

  private lastDrag: { from: string; p: P; touch: boolean } | null = null;

  dragMove(from: string, p: P, touch: boolean): void {
    this.lastDrag = { from, p, touch };
    const target = this.scene.hitNode(p, SNAP_PX - 26, from);
    if (target && target !== this.dragTarget) {
      this.ctx.sfx.play('snap');
      this.ctx.vibrate?.(10);
    }
    this.dragTarget = target;
    const a = this.scene.nodeScreen(from);
    if (a && !target) {
      const seg = Math.floor(Math.hypot(p.x - a.x, p.y - a.y) / (44 * this.scene.k));
      if (seg !== this.dragSeg && seg > 0) this.ctx.sfx.play('stretch', seg);
      this.dragSeg = seg;
    }
    const to = target ? this.scene.nodeScreen(target) : p;
    let ok = false;
    let label = '';
    if (target && this.game.theirs('build_line')) label = 'Planlæggerens job';
    else if (target) {
      const c = chooseBuild(this.game.state, this.game.level.mechanics, from, target, this.shift);
      ok = c.ok;
      const price = c.ok ? (c.cost > 0 ? `${c.cost} kr` : 'Gratis') : '';
      label = c.ok ? (c.medium === 'cable' ? `Kabel ${price}` : c.medium === 'sea' ? `Søkabel ${price}` : price) : shortReason(c.error) || 'Kan ikke';
    }
    this.scene.preview(from, to, ok || !target, target !== null);
    this.scene.loupe(touch ? p : null);
    const b = this.ctx.bubble;
    if (label && to) {
      b.hidden = false;
      b.textContent = label;
      b.className = `bubble ${ok ? 'ok' : 'bad'}`;
      b.style.transform = `translate(${Math.round(to.x)}px, ${Math.round(to.y - 46 * this.scene.k)}px) translate(-50%, -100%)`;
    } else {
      b.hidden = true;
    }
  }

  dragEnd(from: string, _p: P, cancelled: boolean): void {
    this.scene.preview(null, null, false, false);
    this.scene.loupe(null);
    this.ctx.bubble.hidden = true;
    const t = this.dragTarget;
    this.dragTarget = null;
    this.dragFrom = null;
    this.lastDrag = null;
    // Afbrudt af en finger mere (knib): intet valg bliver hængende.
    if (cancelled) {
      this.select(null);
      return;
    }
    // Lidt ved siden af en by tæller også, når fingeren slipper (et bredere net end under trækket).
    const end = t ?? this.scene.hitNode(_p, SNAP_PX - 10, from);
    this.select(null);
    if (end && end !== from) {
      if (!this.build(from, end)) this.dropNote(end, _p);
    } else {
      // Sluppet i det blå: intet valg bliver hængende. Et gammelt valg kaprede ellers næste træk
      // eller tryk (playtest). Sig kort, hvad der skete, lige der hvor fingeren slap.
      this.dropNote(null, _p);
    }
  }

  /** Et træk, der ikke blev til en ledning: en kort boble ved fingeren, så det aldrig sker i stilhed. */
  private dropNote(target: string | null, p: P): void {
    const b = this.ctx.bubble;
    let text = 'Slip på en by eller mølle';
    if (target) text = this.lastFail || 'Kan ikke her';
    const at = target ? (this.scene.nodeScreen(target) ?? p) : p;
    b.hidden = false;
    b.textContent = text;
    b.className = 'bubble bad';
    b.style.transform = `translate(${Math.round(at.x)}px, ${Math.round(at.y - 46 * this.scene.k)}px) translate(-50%, -100%)`;
    window.clearTimeout(this.dropTimer);
    this.dropTimer = window.setTimeout(() => {
      if (!this.dragFrom) b.hidden = true;
    }, 1600);
  }

  private dropTimer = 0;
  /** Den korte grund til det seneste byg, der ikke kunne lade sig gøre (til boblen). */
  private lastFail = '';

  panStart(): void {
    this.scene.cam.stop();
  }

  pan(dx: number, dy: number): void {
    this.scene.cam.panBy(dx, dy);
    if (this.ctx.ring.isOpen) this.ctx.ring.close();
  }

  panEnd(vx: number, vy: number): void {
    this.scene.cam.release(vx, vy);
  }

  zoom(x: number, y: number, factor: number): void {
    this.scene.cam.zoomAt(x, y, factor);
    if (this.ctx.ring.isOpen) this.ctx.ring.close();
  }

  // -------------------------------------------------------------------------
  // Ringmenuer

  /** Driftsvagten med en makker, der har bygge-rollen: tryk og træk på byer bygger aldrig. */
  private get driftOnly(): boolean {
    return this.game.theirs('build_line');
  }

  private item(icon: string, label: string, kind: ActionKind, target: string | undefined, costText: (cost: number) => string, primary = false, askable = true): RingItem {
    // Orkans pause: knappen sætter i kø og tjekkes mod nettet med planen bygget.
    const planned = this.planning && PLANNABLE.has(kind);
    const c = check(planned ? this.preview() : this.game.state, kind, target, undefined);
    const paused = !planned && !this.game.canBuildNow && !((this.game.paused || this.game.cardHold) && this.game.canInPause(kind));
    // Makkerens job: står der og siger, hvem der gør det (IGN-løkke runde 4). Spor: coop: med en makker
    // i rummet er knappen et spørgsmål ("Opgradér her?"), der lander som et ping hos makkeren.
    if (this.game.theirs(kind)) {
      const who = roleOf(kind) === 'p1' ? 'Planlæggeren' : 'Driftsvagten';
      if (askable && target && target !== REINFORCE_ALL && this.ctx.canAsk?.()) {
        return { icon, label: `${label}?`, sub: `Spørg ${this.ctx.partnerName?.() ?? who.toLowerCase()}`, enabled: true, primary: false, run: () => this.askPartner(target, kind) };
      }
      return { icon, label, sub: who, enabled: false, primary: false, run: () => {} };
    }
    return {
      icon,
      label,
      sub: paused ? (this.game.cardHold ? CARD_FIRST_SHORT : shortReason('paused')) : c.ok ? (planned ? `I kø · ${costText(c.cost)}` : costText(c.cost)) : shortReason(c.error, kind),
      enabled: c.ok && !paused,
      primary,
      run: () => {
        this.act(kind, target);
      },
    };
  }

  /** Går ledningen gennem stormsporet (samme regel som sim'ens stormLines, bare for alle medier)? */
  private inTrack(l: Line, trackY: number, half: number): boolean {
    const a = nodeById(this.game.state, l.a);
    const b = nodeById(this.game.state, l.b);
    return !!a && !!b && segmentInBand(a, b, trackY - half, trackY + half);
  }

  private lineMenu(l: Line, p: P): void {
    const mech = this.game.level.mechanics;
    const s = this.game.state;
    const items: RingItem[] = [];
    const storm = s.threats.some((t) => t.kind === 'storm' && t.phase === 'warning' && t.atRisk.includes(l.id));
    // Et kabel i stormens spor: sig, at det klarer sig. Playtest runde 3: Horns Rev-Endrup (søkabel) lå
    // i sporet og viste kun "Opgradér · Fuldt udbygget", så testeren troede, forstærkning var gået i stykker.
    const inBand = l.medium !== 'air' && s.threats.some((t) => t.kind === 'storm' && t.phase === 'warning' && this.inTrack(l, t.trackY, t.trackHalf));
    if (inBand) items.push({ icon: ICON.reinforce, label: 'Stormsikker', sub: l.medium === 'sea' ? 'Søkabler tåler storm' : 'Kabler tåler storm', enabled: false, run: () => {} });
    // Forstærk først og Riv ned sidst, så det farlige valg aldrig står lige ved siden af stormens valg.
    if (mech.includes('reinforce') && l.medium === 'air') items.push(this.item(ICON.reinforce, 'Forstærk', 'reinforce_mast', l.id, () => `${REINFORCE_COST} kr`, storm));
    items.push(this.item(ICON.upgrade, 'Opgradér', 'upgrade_line', l.id, (c) => (c > 0 ? `${c} kr` : 'Gratis'), !storm));
    if (l.halfDown) items.push(this.item(ICON.crew, 'Montør', 'dispatch_crew', l.id, () => 'Gratis', true));
    // Grav ned: en luftledning bliver til jordkabel og er så fri af storme for altid.
    if (mech.includes('cable') && l.medium === 'air' && !l.fixed) items.push(this.item(ICON.bury, 'Grav ned', 'bury_line', l.id, (c) => `${c} kr`));
    // Mens stormen er på vej, er Riv ned aldrig det, man ville: den står ikke i menuen.
    if (l.owner !== 'level' && !l.fixed && !storm) {
      const refund = demolishRefund(s, l);
      // Efter fortryd-vinduet giver nedrivning kun skrotværdien: sig, hvad ledningen kostede, så tabet ses.
      const undo = s.tick - l.builtTick <= UNDO_TICKS;
      const sub = refund > 0 ? (undo ? `+${refund} kr` : `+${refund} af ${l.spent} kr`) : refund < 0 ? `${refund} kr` : '';
      const it = this.item(ICON.demolish, 'Riv ned', 'demolish', l.id, () => sub, false, false);
      it.danger = true;
      it.run = () => {
        const txt = undo || refund <= 0 ? (refund > 0 ? `Revet ned. ${refund} kr tilbage i kassen.` : 'Revet ned.') : `Revet ned. Skrotværdi: ${refund} af ${l.spent} kr tilbage.`;
        if (this.act('demolish', l.id)) this.ctx.toast.say(txt, 'info', 2400);
      };
      items.push(it);
    }
    const a = nodeById(s, l.a)?.name ?? '';
    const b = nodeById(s, l.b)?.name ?? '';
    const tier = ['', '150\u00a0kV', '400\u00a0kV', 'Dobbelt\u00a0400\u00a0kV'][l.tier] ?? '';
    const medium = l.medium === 'air' ? 'luftledning' : l.medium === 'cable' ? 'jordkabel' : 'søkabel';
    // Udlandskablet er jævnstrøm og har intet spændingstrin i spillet (IGN runde 1: "150 kV søkabel").
    const what = l.medium === 'hvdc' ? 'jævnstrømskabel til udlandet' : `${tier} ${medium}`;
    this.openRing(p, `${a} til ${b} · ${what}`, items, this.scene.lineNormal(l.id));
  }

  private nodeMenu(id: string, p: P, title?: string): void {
    if (this.driftOnly) {
      this.driftMenu(id, p);
      return;
    }
    const s = this.game.state;
    const n = nodeById(s, id);
    if (!n) return;
    const items: RingItem[] = [];
    if (this.game.level.mechanics.includes('station')) items.push(this.item(ICON.station, 'Flere pladser', 'upgrade_station', id, () => (stationCost(this.game.state) === 0 ? 'Gratis' : `${stationCost(this.game.state)} kr`), true));
    // Batteri (bane 3-4): lader af overskud og giver strøm tilbage, når byerne mangler. Var i sim'en
    // og i pakken "Stort batteri", men kunne ikke købes nogen steder (playtest).
    if (this.game.level.mechanics.includes('battery') && n.kind !== 'foreign') {
      const free = s.perks.freeBattery > 0;
      const it = this.item(ICON.bolt, free ? 'Stort batteri' : 'Batteri', 'build_battery', id, (c) => (c > 0 ? `${c} kr` : 'Gratis'), free);
      if (n.battery) it.sub = `${Math.round((n.battery.store / Math.max(1, n.battery.storeMax)) * 100)} % ladet`;
      it.run = () => {
        if (!this.act('build_battery', id)) return;
        this.ctx.toast.say(`Batteriet i ${n.name} lader af overskud og hjælper, når byerne mangler strøm.`, 'good', 3200);
      };
      items.push(it);
    }
    // Svinghjul (bane 6): tung roterende masse, der tager frekvensdykket, når et værk falder ud.
    if (this.game.level.mechanics.includes('flywheel') && n.kind !== 'foreign') {
      const free = s.perks.freeFlywheel > 0;
      const it = this.item(ICON.flywheel, 'Svinghjul', 'build_flywheel', id, (c) => (c > 0 ? `${c} kr` : 'Gratis'), free || s.threats.some((t) => t.kind === 'plant_trip' && t.phase === 'warning' && t.area === n.area));
      if (n.flywheels > 0) it.sub = n.flywheels >= 2 ? 'To snurrer' : `${it.sub} · 1 snurrer`;
      it.run = () => {
        if (!this.act('build_flywheel', id)) return;
        this.ctx.toast.say(n.area === 'DK2' ? 'Svinghjulet snurrer. Østdanmarks frekvens falder nu langsommere.' : 'Svinghjulet snurrer. Frekvensen falder nu langsommere, når et værk ryger.', 'good', 3200);
      };
      items.push(it);
    }
    items.push({
      icon: ICON.line,
      label: 'Byg herfra',
      sub: 'Tryk på målet',
      enabled: this.game.canBuildNow || this.planning,
      run: () => this.select(id),
    });
    const at = this.scene.nodeScreen(id) ?? p;
    this.openRing(at, title ?? n.name, items);
  }

  /** Spor: coop: send spørgsmålet og sig ærligt, at det er sendt (ikke gjort). */
  private askPartner(at: string, kind: ActionKind): void {
    if (!this.ctx.ask?.(at, kind)) return;
    // Volt svarer selv med det samme i sin boble ("Jeg tager den" eller "Ikke nu, fordi ...").
    if (this.ctx.partnerName?.()) return;
    this.ctx.toast.say(roleOf(kind) === 'p1' ? 'Spurgt. Planlæggeren ser det med det samme.' : 'Spurgt. Driftsvagten ser det med det samme.', 'info', 1800);
  }

  /**
   * Spor: coop: Driftsvagtens ring på en by. Egne knapper på byens egne ledninger (forstærk, montør,
   * jag gæsten, nødknappen) og "Byg her?" til Planlæggeren. Ingen byggeflade, der afvises bagefter.
   */
  private driftMenu(id: string, p: P): void {
    const s = this.game.state;
    const n = nodeById(s, id);
    if (!n) return;
    const mech = this.game.level.mechanics;
    const own = s.lines.filter((l) => l.a === id || l.b === id);
    const ownIds = new Set(own.map((l) => l.id));
    const items: RingItem[] = [];
    if (mech.includes('reinforce')) {
      const risky = (l: Line): boolean => s.threats.some((t) => t.phase === 'warning' && t.atRisk.includes(l.id));
      const air = own.filter((l) => l.medium === 'air' && !l.reinforced && l.status === 'ok').sort((a, b) => Number(risky(b)) - Number(risky(a)));
      const l = air[0];
      if (l) items.push(this.item(ICON.reinforce, 'Forstærk', 'reinforce_mast', l.id, () => `${REINFORCE_COST} kr`, risky(l)));
    }
    const hurt = own.find((l) => l.status === 'broken' || l.halfDown);
    if (hurt) items.push(this.item(ICON.crew, 'Montør', 'dispatch_crew', hurt.id, () => 'Gratis', true));
    else if (mech.includes('crew')) items.push(this.item(ICON.crew, 'Hyr montør', 'hire_crew', undefined, (c) => `${c} kr`));
    const guest = s.threats.find((t) => (t.atRisk.some((x) => ownIds.has(x)) || ownIds.has(t.targetLine) || t.nodes?.includes(id)) && check(s, 'shoo_threat', t.id, undefined).ok);
    if (guest) items.push(this.item(ICON.hand, 'Jag væk', 'shoo_threat', guest.id, () => 'Gratis', true));
    if (mech.includes('shed') && n.city) items.push(this.item(ICON.bolt, 'Nødaflastning', 'power_shed', id, () => 'Nødknap'));
    if (n.kind !== 'foreign') {
      items.push(
        this.ctx.canAsk?.()
          ? { icon: ICON.line, label: 'Byg her?', sub: `Spørg ${this.ctx.partnerName?.() ?? 'planlæggeren'}`, enabled: true, run: () => this.askPartner(id, 'build_line') }
          : { icon: ICON.line, label: 'Byg', sub: 'Planlæggeren', enabled: false, run: () => {} },
      );
    }
    if (n.city && this.ctx.inspect) items.push({ icon: ICON.target, label: 'Se byen', sub: '', enabled: true, run: () => this.ctx.inspect?.(id) });
    this.scene.cities.squash(id);
    const at = this.scene.nodeScreen(id) ?? p;
    this.openRing(at, `${n.name} · Driftsvagt`, items);
  }

  /** Åbn ringen. Med `normal` (en ledning) skubbes buen lidt væk fra ledningen, så den kan ses. */
  private openRing(p: P, title: string, items: RingItem[], normal?: P | null): void {
    const r = this.ctx.stage.getBoundingClientRect();
    const area = this.ctx.ringArea?.() ?? { left: r.left + 8, top: r.top + 8, right: r.right - 8, bottom: r.bottom - 8 };
    this.ctx.ring.open(p.x + r.left, p.y + r.top, title, items, area, normal ?? undefined);
    this.ctx.sfx.play('tap');
  }

  // -------------------------------------------------------------------------
  // Tastatur

  setTool(_t: Tool): void {
    this.select(null);
    this.ctx.ring.close();
    // Hintet nederst siger, hvad værktøjet gør; en gammel toast fra forrige værktøj skal væk.
    this.ctx.toast.clear();
  }

  /** Shift skifter mellem luftledning og jordkabel, også midt i et træk. */
  private setShift(on: boolean): void {
    if (this.shift === on) return;
    this.shift = on;
    const sel = this.scene.selected;
    if (sel) this.scene.targets = this.buildTargets(sel);
    const d = this.lastDrag;
    if (d && this.dragFrom) this.dragMove(d.from, d.p, d.touch);
  }

  private keyUp = (e: KeyboardEvent): void => {
    if (e.key === 'Shift') this.setShift(false);
  };

  /** Hvornår tasten 4 sidst spurgte om en montør (0 = ikke spurgt). */
  private crewAskedAt = 0;

  private key = (e: KeyboardEvent): void => {
    if (e.key === 'Shift') {
      if (this.game.level.mechanics.includes('cable')) this.setShift(true);
      return;
    }
    const tag = (e.target as HTMLElement | null)?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA') return;
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
      e.preventDefault();
      if (this.ctx.active() && !this.game.undo()) this.ctx.toast.say('Der er intet at fortryde.', 'info', 1400);
      return;
    }
    // Escape lukker kun: ringen, valget, et værktøj, en skærm. Den pauser aldrig.
    if (e.key === 'Escape') {
      if (this.ctx.ring.isOpen) {
        this.ctx.ring.close();
        return;
      }
      if (this.scene.selected) {
        this.select(null);
        return;
      }
      if (this.ctx.escape()) return;
      if (this.ctx.active() && this.ctx.bar.tool !== 'build') this.ctx.bar.pick('build');
      return;
    }
    if (!this.ctx.active()) return;
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    const mech = this.game.level.mechanics;
    const onStage = e.target === document.body || e.target === this.ctx.stage || e.target === this.ctx.ring.el;
    const k = e.key.toUpperCase();
    // Et åbent hændelseskort svarer på A, S og D; Nødaflastningens byliste på tallene.
    const cardKey = ['A', 'S', 'D'].indexOf(k);
    if (cardKey >= 0 && this.ctx.cardPanel?.isOpen) {
      e.preventDefault();
      this.ctx.cardPanel.answerIndex(cardKey);
      return;
    }
    if (/^[1-9]$/.test(e.key) && this.ctx.powers?.isPicking) {
      e.preventDefault();
      this.ctx.powers.pickIndex(Number(e.key) - 1);
      return;
    }
    if (k === 'R' && this.ctx.powers) {
      e.preventDefault();
      const why = this.ctx.powers.pressHz();
      if (why) {
        this.ctx.toast.say(why, 'warn');
        this.ctx.sfx.play('error');
      }
      return;
    }
    const power = ({ Q: 'fcr', W: 'shed', E: 'norway' } as Record<string, PowerId | undefined>)[k];
    if (power && this.ctx.powers) {
      e.preventDefault();
      this.ctx.ring.close();
      const why = this.ctx.powers.press(power);
      if (why) {
        this.ctx.toast.say(why, 'warn');
        this.ctx.sfx.play('error');
      }
      return;
    }
    if (e.key === '1') {
      if (this.ctx.bar.tool !== 'build') this.ctx.bar.pick('build');
    } else if (e.key === '2' && mech.includes('upgrade')) {
      this.ctx.bar.pick('upgrade');
    } else if (e.key === '3' && mech.includes('station')) {
      this.ctx.bar.pick('station');
    } else if (e.key === '4' && mech.includes('crew')) {
      // En montør koster penge: tasten spørger først og hyrer ved andet tryk.
      const now = performance.now();
      if (now - this.crewAskedAt < 2500) {
        this.crewAskedAt = 0;
        this.act('hire_crew');
      } else {
        const c = check(this.game.state, 'hire_crew', undefined, undefined, this.game.player);
        if (!c.ok) {
          this.act('hire_crew');
          return;
        }
        this.crewAskedAt = now;
        this.ctx.toast.say(`Tryk 4 igen for at hyre en montør til ${c.cost} kr.`, 'info', 2500);
      }
    } else if (e.key === '5' && mech.includes('reinforce')) {
      // Co-op-anmelderen (IGN-løkke runde 3): knappen viste "5", men tasten gjorde intet.
      if (exposedLines(this.game.state).length === 0) this.ctx.toast.say('Ingen storm truer masterne lige nu.', 'info', 1800);
      else this.act('reinforce_mast', REINFORCE_ALL);
    } else if (e.key === ' ' && (onStage || spaceIsPause(e.target))) {
      // Mellemrum er pause, også når en knap i HUD'en eller værktøjslinjen har fokus (IGN runde 1:
      // "mellemrum trykkede på den knap, jeg sidst klikkede"). Fokus slippes, så knappen ikke klikkes ved keyup.
      e.preventDefault();
      if (!onStage && e.target instanceof HTMLElement) e.target.blur();
      this.ctx.togglePause();
    } else if (e.key === 'p' || e.key === 'P') {
      this.ctx.togglePause();
    } else if (e.key === 'f' || e.key === 'F') {
      this.ctx.toggleSpeed();
    } else if (e.key === 'n' || e.key === 'N') {
      this.ctx.callYear();
    }
  };
}
